import { useGetUrl } from '@seek/ca-myprofile-utils';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Inline,
  TextLink,
  Button,
  Stack,
  Loader,
  Heading,
  FieldLabel,
} from 'braid-design-system';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useAppConfig } from 'src/modules/appConfig';
import { hotjarEvent } from 'src/modules/hotjar';

import { ManageUrl } from '../ManageUrl/ManageUrl';
import { SharedProfileVisibility } from '../SharedProfileVisibilityRadio/SharedProfileVisibilityRadio';
import {
  useCreateShareableProfileUrl,
  useGetShareableProfileStatus,
} from '../data';

import translations from './.vocab';

export const ShareableProfileSettings = () => {
  const { t } = useTranslations(translations);
  const { zone, language, brand, isUnifiedSite } = useAppConfig();
  const getUrl = useGetUrl(zone, language, isUnifiedSite);
  const {
    isShareableProfileActive,
    shareableProfileUrl,
    shareableProfileDisplayUrl,
    loading,
    error,
  } = useGetShareableProfileStatus();
  const { shareableProfileButtonPressed } = useAnalytics();

  const [
    createShareableProfileUrl,
    {
      loading: createShareableProfileLoading,
      error: createShareableProfileError,
    },
  ] = useCreateShareableProfileUrl({
    key: 'profile-visibility-v2',
  });

  const onClickHandler = () => {
    shareableProfileButtonPressed({
      eventName: 'profile_update_succeeded',
      isEditState: true,
    });
    createShareableProfileUrl({ zone, languageCode: language });
    hotjarEvent('shareable-profile-created');
    metrics.count('shareable_profile_generated');
  };

  const hasNoShareableProfile =
    shareableProfileUrl === null || shareableProfileDisplayUrl === null;

  return loading || error ? (
    <Loader size="small" />
  ) : (
    <Stack space="large">
      <Stack space={shareableProfileUrl ? 'xxsmall' : 'small'}>
        <Heading level="4">{t('Share brand profile', { brand })}</Heading>
      </Stack>
      {hasNoShareableProfile ? (
        <>
          <Text tone="secondary">
            {t('Once you create this link, others can view...', {
              TextLink: (children) => (
                <TextLink
                  weight="weak"
                  hitArea="large"
                  href={getUrl('shareable-profile-faq-url')}
                >
                  {children}
                </TextLink>
              ),
            })}
          </Text>
          <Inline space="small" collapseBelow="tablet">
            <Button
              id="consent-create-btn"
              data={{ automation: 'consent-create-btn' }}
              tone="formAccent"
              variant="ghost"
              onClick={onClickHandler}
              loading={
                createShareableProfileError
                  ? false
                  : createShareableProfileLoading
              }
            >
              {t('Consent and create link')}
            </Button>
          </Inline>
        </>
      ) : (
        <Stack space="large">
          <Text tone="secondary">
            {t('You can share this link to...', {
              TextLink: (children) => (
                <TextLink
                  weight="weak"
                  hitArea="large"
                  target="_blank"
                  href={getUrl('shareable-profile-faq-url')}
                >
                  {children}
                </TextLink>
              ),
            })}
          </Text>
          <Stack space="xsmall">
            <FieldLabel
              label={t('Your Shared Profile link')}
              htmlFor="manage-url-txt"
            />
            <ManageUrl
              mode="EDIT"
              profileUrl={shareableProfileUrl}
              profileDisplayUrl={shareableProfileDisplayUrl}
              isActive={isShareableProfileActive}
            />
          </Stack>
          <SharedProfileVisibility />
        </Stack>
      )}
    </Stack>
  );
};
